import { useParams } from "react-router-dom";
import { findWhere } from "underscore";

import FiltersButtonWithOptions from "../../../SavedFilters/FiltersButtonWithOptions";
import useUserActivity from "../../../../api/api-hooks/userActivity/useUserActivity";
import useUpdateView from "../../../../api/api-hooks/views/useUpdateView";
import useReadableText from "../../../../hooks/useReadableText";
import usePage from "../../../../api/api-hooks/pages/usePage";
import { Module } from "../../../../types/TenantInterfaces";
import { View } from "../../../../types/PageInterfaces";
import usePageViewTable from "../usePageViewTable";
import StrUtils from "../../../../utils/StrUtils";

interface Props {
  currentModule: Module;
  query: any;
  setQuery: (q: any) => void;
  simpleSearchValue: string;
  setSimpleSearchValue: (val: string) => void;
  clearSimpleSearch: () => void;
  view: View;
  parentViewID?: string;
  disableSave?: boolean;
  disableAdvancedFilter?: boolean;
}

const SharedFiltersButtonWithOptions = ({
  currentModule,
  query,
  setQuery,
  simpleSearchValue,
  setSimpleSearchValue,
  clearSimpleSearch,
  view,
  parentViewID,
  disableSave,
  disableAdvancedFilter,
}: Props) => {
  const { pageID }: any = useParams();
  const { page } = usePage(pageID);
  const { setUserActivity } = useUserActivity();
  const { updateView, viewUpdating } = useUpdateView(pageID);
  const { getReadableText } = useReadableText({
    query,
    moduleAPIName: currentModule?.APIName,
    fallbackMessage: "None",
  });

  // Functions for Quick Creates
  const { createItem, updateItem, deleteItem, reorderItems } = usePageViewTable(
    {
      currentViewID: view?._id || "",
      updateView,
      parentViewID,
    }
  );

  return (
    <FiltersButtonWithOptions
      currentModule={currentModule}
      //@ts-ignore
      stateQuery={view?.Query ?? {}}
      //@ts-ignore
      filters={view?.SavedFilters ?? []}
      query={query}
      setQuery={setQuery}
      onApply={checkSimpleSearchQuery}
      onClear={clearSimpleSearch}
      onSave={updateDefaultFilters}
      submitting={viewUpdating}
      clearFilter={clearFilter}
      createFilter={createItem}
      updateFilter={updateItem}
      deleteFilter={deleteItem}
      reorderFilters={reorderItems}
      title="Page Filters"
      updateKey="SavedFilters"
      createKey="SavedFilter"
      emptyDescription="No page filters available. Get started by adding a new one."
      view={view}
      page={page}
      showVariables={true}
      disableAdvancedFilter={disableAdvancedFilter}
      disableSave={disableSave}
    />
  );

  function checkSimpleSearchQuery(query: any) {
    if (view && view.id) {
      // Set User Activity "queried-page-view-table" when the queried records change
      const queriedPageViewTableActivity = {
        id: StrUtils.uuidv4(),
        Type: "queried-page-view-table",
        Page: {
          id: page?.id,
          Name: page?.Name,
          Logo: page?.Icon,
        },
        View: {
          id: view?.id,
          Name: view?.Name,
          Type: view?.Type,
        },
        Readable: getReadableText(query),
      };
      // @ts-ignore TODO: check this - params seem correct but erroring.
      setUserActivity({
        Activity: queriedPageViewTableActivity,
      });
    }

    if (!query?.Filters?.length) {
      return setSimpleSearchValue("");
    }

    if (
      query?.Filters?.length === 1 &&
      !findWhere(query?.Filters[0]?.Filters, { Field: "_RecordName" })
    ) {
      return setSimpleSearchValue("");
    }

    let found = 0;
    let previous = simpleSearchValue ?? "";

    for (let i = 0; i < query?.Filters?.length; i++) {
      const existing = findWhere(query?.Filters[i]?.Filters, {
        Field: "_RecordName",
      });

      if (!existing) continue;

      found++;

      if (!existing?.Value || (previous && previous !== existing?.Value)) {
        return setSimpleSearchValue("");
      }

      previous = existing?.Value;
    }

    if (found !== query?.Filters?.length) {
      return setSimpleSearchValue("");
    }
  }

  function updateDefaultFilters(filters: any, type: any) {
    if (!view || !view.id) return;

    const query = view?.Query;

    query.Filters = filters?.length ? filters : [];

    query.Metadata = { Default: true };
    type && (query.Metadata.Type = type);

    // @ts-ignore
    updateView({
      viewID: view?.id,
      payload: { Query: query },
      onSuccess: () => {
        setQuery(query);
      },
      parentViewID,
    });
  }

  function clearFilter() {
    let newQuery = { ...query };

    if (!newQuery) {
      newQuery = {};
    }

    newQuery.Filters = [];
    newQuery.Limit = {};

    if (newQuery?.Metadata?.PageFilter) {
      delete newQuery.Metadata.PageFilter;
    }

    setQuery(newQuery);
  }
};

export default SharedFiltersButtonWithOptions;
